import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  ResponsiveList,
  Skeleton,
  StatusBadge,
  formatDate,
  useMediaQuery,
  useToast,
  todayInArgentina,
  type ActionBarAction,
  type ResponsiveListColumn,
} from '@catering/ui';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { FilePlus2, Printer, Search, Truck } from 'lucide-react';
import { useState, type JSX } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { hasPermission } from '../../auth/has-permission';
import { listSites, searchAllCustomers } from '../../customers/api';
import {
  DELIVERY_NOTE_STATUSES,
  generateDeliveryNotes,
  listDeliveryNotes,
  type DeliveryNote,
  type DeliveryNoteStatus,
} from '../../delivery-notes/api';
import { mapDeliveryNoteError } from '../../delivery-notes/delivery-note-api-errors';
import { ListLoadError } from '../../components/list-load-error';
import { GuideLink } from '../../guide/guide-link';
import { OperatingUnitSelect } from '../../components/operating-unit-select';
import { useOperatingUnitOptions } from '../../operating-units/use-operating-unit-options';

function todayIso(): string {
  return todayInArgentina();
}

const deliveryNotesRoute = getRouteApi('/protected/delivery-notes');

const STATUS_LABELS: Record<DeliveryNoteStatus, string> = {
  PENDING: 'Pendiente',
  DISPATCHED: 'Despachado',
  DELIVERED: 'Entregado',
  SIGNED: 'Firmado',
  INVOICED: 'Facturado',
  VOID: 'Anulado',
};

interface FilterPatch {
  serviceDate?: string;
  siteId?: string | undefined;
  status?: DeliveryNoteStatus | undefined;
  sinFirmar?: boolean;
}

/** Table columns (≥640px, unchanged): row rendering only, not reused by the card layout. */
const deliveryNoteColumns: ResponsiveListColumn<DeliveryNote>[] = [
  {
    key: 'number',
    header: 'Número',
    render: (note) => (
      <Link
        to="/delivery-notes/$id"
        params={{ id: note.id }}
        className="font-medium text-slate-900 hover:underline"
      >
        {note.noteNumber}
      </Link>
    ),
  },
  { key: 'company', header: 'Empresa', render: (note) => note.customerNameSnapshot },
  { key: 'site', header: 'Sede', render: (note) => note.siteNameSnapshot },
  {
    key: 'date',
    header: 'Fecha de servicio',
    render: (note) => formatDate(note.serviceDate),
  },
  {
    key: 'status',
    header: 'Estado',
    render: (note) => <StatusBadge domain="deliverynote" status={note.status} />,
  },
];

/** Only offered with more than one visible unit; the name is the one frozen on the note. */
const unitColumn: ResponsiveListColumn<DeliveryNote> = {
  key: 'unit',
  header: 'Punto de venta',
  render: (note) => note.operatingUnitNameSnapshot ?? '—',
};

/**
 * `delivery-note-documents/spec.md` → "Listado de remitos con filtros". List
 * state lives in the URL, same convention as `orderListSearchSchema`.
 * "Sin firmar" is a quick filter that overrides `status` with `DELIVERED`
 * (`design.md` §9.1/§9.2) — it is not itself sent to the API.
 */
export function DeliveryNoteListPage(): JSX.Element {
  const search = deliveryNotesRoute.useSearch();
  const navigate = deliveryNotesRoute.useNavigate();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateServiceDate, setGenerateServiceDate] = useState(todayIso());

  const generateMutation = useMutation({
    mutationFn: (serviceDate: string) => generateDeliveryNotes(serviceDate),
    onSuccess: async (result, serviceDate) => {
      setGenerateModalOpen(false);
      // Show what was just generated, whatever date the list was on.
      void navigate({ search: (prev) => ({ ...prev, serviceDate }) });
      await queryClient.invalidateQueries({ queryKey: ['delivery-notes'] });
      toast(
        'success',
        result.created.length === 0
          ? 'No había pedidos confirmados sin remito para esa fecha'
          : `Se generaron ${result.created.length} remitos`,
      );
    },
  });

  const effectiveStatus = search.sinFirmar ? 'DELIVERED' : search.status;

  // Not URL-owned: the unit filter and column only exist with more than one visible unit.
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const showUnits = useOperatingUnitOptions().length > 1;
  const columns = showUnits
    ? [...deliveryNoteColumns.slice(0, 3), unitColumn, ...deliveryNoteColumns.slice(3)]
    : deliveryNoteColumns;

  const query = useQuery({
    queryKey: [
      'delivery-notes',
      search.serviceDate,
      search.siteId,
      operatingUnitId,
      effectiveStatus,
    ],
    queryFn: () =>
      listDeliveryNotes({
        serviceDateFrom: search.serviceDate,
        serviceDateTo: search.serviceDate,
        siteId: search.siteId,
        operatingUnitId: operatingUnitId === '' ? undefined : operatingUnitId,
        status: effectiveStatus,
        page: 1,
        pageSize: 100,
      }),
    placeholderData: keepPreviousData,
  });

  const customersQuery = useQuery({
    queryKey: ['customers-for-delivery-notes'],
    queryFn: () => searchAllCustomers({ q: undefined, zone: undefined, status: undefined }),
  });

  // `customerId` is not URL-owned: `design.md` §9.1's search schema has no
  // `customerId` param, only `siteId`. The selected company for narrowing
  // the "Sede" picker stays in local state.
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | undefined>(undefined);

  const sitesQuery = useQuery({
    queryKey: ['sites-for-delivery-notes', selectedCustomerId],
    queryFn: () => listSites(selectedCustomerId ?? ''),
    enabled: selectedCustomerId !== undefined,
  });

  const customers = customersQuery.data?.items ?? [];
  const sites = sitesQuery.data ?? [];

  const canExport = hasPermission(currentUser.data, 'deliverynote.export');
  const canCreate = hasPermission(currentUser.data, 'deliverynote.create');
  const isDesktop = useMediaQuery('(min-width: 640px)');

  const generateAction: ActionBarAction = {
    label: 'Generar remitos',
    icon: FilePlus2,
    onClick: () => setGenerateModalOpen(true),
  };
  const printAction: ActionBarAction = {
    label: 'Imprimir remitos del día',
    icon: Printer,
    variant: 'secondary',
    onClick: () => void navigate({ to: '/delivery-notes/print-batch' }),
  };
  const primaryAction = canCreate ? generateAction : canExport ? printAction : undefined;
  const secondaryActions = canCreate && canExport ? [printAction] : [];

  function applyFilters(patch: FilterPatch): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  const hasActiveFilters =
    search.siteId !== undefined ||
    operatingUnitId !== '' ||
    search.status !== undefined ||
    search.sinFirmar;

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={Truck}
        title="Remitos"
        description="Consultá el listado de remitos y su estado de despacho, entrega y firma."
        actions={
          <div className="flex items-center gap-2">
            {isDesktop ? (
              <>
                {canCreate ? (
                  <Button icon={FilePlus2} onClick={() => setGenerateModalOpen(true)}>
                    Generar remitos
                  </Button>
                ) : null}
                {canExport ? (
                  <Link to="/delivery-notes/print-batch">
                    <Button variant="secondary" icon={Printer}>
                      Imprimir remitos del día
                    </Button>
                  </Link>
                ) : null}
              </>
            ) : primaryAction !== undefined ? (
              <ActionBar primary={primaryAction} secondary={secondaryActions} />
            ) : null}
            <GuideLink moduleId="delivery-notes" />
          </div>
        }
      />

      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Fecha de servicio</span>
            <input
              type="date"
              value={search.serviceDate}
              onChange={(event) => applyFilters({ serviceDate: event.target.value })}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            />
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Empresa</span>
            <select
              value={selectedCustomerId ?? ''}
              onChange={(event) => {
                const value = event.target.value === '' ? undefined : event.target.value;
                setSelectedCustomerId(value);
                applyFilters({ siteId: undefined });
              }}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todas</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.legalName}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Sede</span>
            <select
              value={search.siteId ?? ''}
              disabled={selectedCustomerId === undefined}
              onChange={(event) =>
                applyFilters({ siteId: event.target.value === '' ? undefined : event.target.value })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50"
            >
              <option value="">Todas</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </label>

          <OperatingUnitSelect
            id="delivery-note-operating-unit"
            label="Punto de venta"
            allLabel="Todos los puntos"
            value={operatingUnitId}
            onChange={setOperatingUnitId}
          />

          <label className="flex flex-col gap-1.5 text-sm">
            <span className="font-medium text-slate-900">Estado</span>
            <select
              value={search.status ?? ''}
              disabled={search.sinFirmar}
              onChange={(event) =>
                applyFilters({
                  status:
                    event.target.value === ''
                      ? undefined
                      : (event.target.value as DeliveryNoteStatus),
                })
              }
              className="h-11 rounded-md border border-slate-300 px-3 text-sm disabled:bg-slate-50"
            >
              <option value="">Todos</option>
              {DELIVERY_NOTE_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={search.sinFirmar}
              onChange={(event) => applyFilters({ sinFirmar: event.target.checked })}
              className="size-4 rounded border-slate-300"
            />
            <span className="font-medium text-slate-900">Sin firmar</span>
          </label>
        </div>
      </Card>

      {query.isError ? (
        <ListLoadError
          title="No pudimos cargar los remitos"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton data-testid="delivery-notes-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="delivery-notes-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="delivery-notes-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="delivery-notes-skeleton-row" className="h-10 w-full" />
          <Skeleton data-testid="delivery-notes-skeleton-row" className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && query.data.total > query.data.items.length ? (
        <Alert tone="warning">
          Mostrando {query.data.items.length} de {query.data.total} remitos. Filtrá por empresa, sede o
          punto de venta para ver el resto.
        </Alert>
      ) : null}

      {query.data !== undefined ? (
        <ResponsiveList
          items={query.data.items}
          rowKey={(note) => note.id}
          columns={columns}
          renderCardHeader={(note) => (
            <Link
              to="/delivery-notes/$id"
              params={{ id: note.id }}
              className="block text-slate-900"
            >
              {note.noteNumber}
            </Link>
          )}
          renderCardBody={(note) => (
            <>
              <span className="text-slate-500">Empresa</span>
              <span>{note.customerNameSnapshot}</span>
              <span className="text-slate-500">Sede</span>
              <span>{note.siteNameSnapshot}</span>
              {showUnits ? (
                <>
                  <span className="text-slate-500">Punto de venta</span>
                  <span>{note.operatingUnitNameSnapshot ?? '—'}</span>
                </>
              ) : null}
              <span className="text-slate-500">Fecha</span>
              <span>{formatDate(note.serviceDate)}</span>
              <span className="text-slate-500">Estado</span>
              <StatusBadge domain="deliverynote" status={note.status} />
            </>
          )}
          getRowTestId={(note) => `delivery-note-card-${note.id}`}
          caption="Remitos"
          emptyState={
            <EmptyState
              icon={hasActiveFilters ? Search : Truck}
              title={
                hasActiveFilters
                  ? 'No se encontraron remitos.'
                  : 'Todavía no hay remitos generados.'
              }
            />
          }
        />
      ) : null}

      {generateModalOpen ? (
        <Modal
          open
          onClose={() => setGenerateModalOpen(false)}
          title="Generar remitos"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {generateMutation.isError ? (
              <Alert tone="danger">{mapDeliveryNoteError(generateMutation.error)}</Alert>
            ) : null}
            <Field
              label="Fecha de servicio"
              name="generateServiceDate"
              type="date"
              required
              value={generateServiceDate}
              onChange={(event) => setGenerateServiceDate(event.target.value)}
            />
            <div className="flex items-center gap-3">
              <Button
                loading={generateMutation.isPending}
                onClick={() => generateMutation.mutate(generateServiceDate)}
              >
                Confirmar
              </Button>
              <Button variant="secondary" onClick={() => setGenerateModalOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
